import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import translations from './DataObjectDescriptionFilter.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import styled from 'styled-components';
import { Flex } from 'antd';
import {
  DataObjectOutputType,
  type GroupDescriptionRef,
  type Frequency,
} from '../dataobject';
import {
  getGroupDescriptionFrequency,
  type GroupDescriptionOption,
  GroupDescriptionOptions,
} from '../sample';
import { FormField, FormSelect } from '../common/form';

const { addTranslations, t } = i18nNsFactory(
  'visual-validation-data-object-description-filter',
);
addTranslations(translations);

type Props = {
  options: GroupDescriptionOptions;
  isLoading: boolean;
  outputType?: DataObjectOutputType;
  onChange?: () => void;
  onFrequencyChange?: (frequency?: Frequency) => void;
};

export const DataObjectDescriptionFilter = ({
  options,
  isLoading,
  outputType,
  onChange,
  onFrequencyChange,
}: Props) => {
  const [_, ungroupedDescriptionOptions, groupedDescriptionOptions] = options;
  const groupDescriptionRef = useWatch({ name: 'groupDescriptionRef' });
  const validGroupDescriptionRefs = useMemo(
    () =>
      new Set(
        [...ungroupedDescriptionOptions, ...groupedDescriptionOptions].map(
          (option) => option.value.toString(),
        ),
      ),
    [ungroupedDescriptionOptions, groupedDescriptionOptions],
  );
  const { getValues } = useFormContext();
  useEffect(() => {
    if (!onFrequencyChange) {
      return;
    }

    const groupDescriptionRef = getValues('groupDescriptionRef');
    if (groupDescriptionRef) {
      onFrequencyChange(
        getGroupDescriptionFrequency(groupDescriptionRef, [
          [],
          ungroupedDescriptionOptions,
          groupedDescriptionOptions,
        ]),
      );
    }
  }, [
    onFrequencyChange,
    getValues,
    ungroupedDescriptionOptions,
    groupedDescriptionOptions,
  ]);

  const { setValue } = useFormContext();

  // Reset filters if the selected groupDescriptionRef is invalid
  useEffect(() => {
    if (isLoading || !groupDescriptionRef) {
      return;
    }

    if (validGroupDescriptionRefs.has(groupDescriptionRef.toString())) {
      return;
    }

    setValue('groupDescriptionRef', undefined);
    setValue('valueDate', undefined);
    setValue('version', undefined);
    onChange?.();
    onFrequencyChange?.(undefined);
  }, [
    isLoading,
    groupDescriptionRef,
    validGroupDescriptionRefs,
    setValue,
    onChange,
    onFrequencyChange,
  ]);

  return (
    <Flex gap="small" wrap="wrap">
      <StyledFormField
        name="groupDescriptionRef"
        label={t(
          `fields.groupDescriptionRef.label${outputType === DataObjectOutputType.REPORT ? 'Report' : ''}`,
        )}
        size="small"
        required
      >
        <FormSelect
          name="groupDescriptionRef"
          placeholder={t(
            `fields.groupDescriptionRef.placeholder${outputType === DataObjectOutputType.REPORT ? 'Report' : ''}`,
          )}
          styles={{
            popup: { root: { maxHeight: 400 } },
          }}
          showSearch
          allowClear={false}
          disabled={isLoading}
          loading={isLoading}
          options={
            isLoading
              ? []
              : [
                  {
                    key: 'grouped-descriptions',
                    label: t('option-group.grouped-descriptions'),
                    options: groupedDescriptionOptions,
                  },
                  {
                    key: 'ungrouped-descriptions',
                    label: t('option-group.ungrouped-descriptions'),
                    options: ungroupedDescriptionOptions,
                  },
                ]
          }
          filterOption={(input, option) =>
            Boolean(option?.label?.toLowerCase().includes(input.toLowerCase()))
          }
          onChange={(
            _value?: number | GroupDescriptionRef,
            option?: GroupDescriptionOption,
          ) => {
            setValue('valueDate', undefined);
            setValue('version', null);
            onChange?.();
            onFrequencyChange?.(option?.frequency);
          }}
        />
      </StyledFormField>
    </Flex>
  );
};

const StyledFormField = styled(FormField)`
  width: 455px;
`;
